import { useRef, useState } from 'react';

/**
 * useCurtainPress — the lobby's "Raise the curtain" press, for BOTH lobby
 * screens (hand/LobbyScreen + discord LobbyContent). One behaviour, one place.
 *
 * Below the player floor the button is deliberately NOT `disabled`: a disabled
 * button eats the tap, and the refusal should be audible — START_GAME is sent,
 * the engine rejects it without changing state, and the Ringmaster explains why.
 * The shake is the local half of that answer.
 *
 * But a table that hammers the button used to get one full narrated line PER
 * press, so the Ringmaster was still explaining long after they stopped. So the
 * refusal is throttled: every press shakes (instant feedback, never swallowed),
 * while the narrated half fires at most once per {@link REFUSAL_NARRATION_MS}.
 * The newest press inside that window is simply absorbed — nothing queues.
 *
 * Above the floor there is no throttle: a real start is a real start.
 */

/** How long a narrated refusal "covers" — later presses shake only. */
export const REFUSAL_NARRATION_MS = 6000;

/** How long the button shakes (matches the `lobby-shake` keyframes). */
const SHAKE_MS = 460;

export function useCurtainPress(
  canStart: boolean,
  onStart: (() => void) | undefined,
): { shake: boolean; press: () => void } {
  const [shake, setShake] = useState(false);
  const lastRefusalAt = useRef(0);

  function press(): void {
    if (canStart) {
      onStart?.();
      return;
    }
    setShake(true);
    window.setTimeout(() => setShake(false), SHAKE_MS);
    const now = Date.now();
    if (now - lastRefusalAt.current < REFUSAL_NARRATION_MS) return; // shake only
    lastRefusalAt.current = now;
    onStart?.();
  }

  return { shake, press };
}
